import { Link } from '@tanstack/react-router'
import { Bell, CheckCheck } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useMarkRead, useNotifications, useUnreadCount } from '../api'
import { unreadBadge } from '../lib'
import { NotificationItem } from './notification-item'

/** Topbar bell: unread count (polled) and the latest notifications in a popover. */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const unread = useUnreadCount()
  const list = useNotifications(false, open)
  const markRead = useMarkRead()
  const badge = unreadBadge(unread.data)
  const items = list.data?.pages.flatMap((p) => p.data).slice(0, 8) ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={badge ? `Notifications (${badge} unread)` : 'Notifications'}>
          <Bell />
          {badge && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] leading-4 font-semibold text-white">{badge}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(380px,calc(100vw-24px))] p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <p className="text-[13px] font-semibold">Notifications</p>
          <Button variant="ghost" size="sm" disabled={!badge || markRead.isPending} onClick={() => markRead.mutate('all')}>
            <CheckCheck className="size-4" /> Mark all read
          </Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-1">
          {list.isLoading ? (
            <div className="grid gap-2 p-2">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-8 text-center text-[13px] text-muted-foreground">You’re all caught up.</p>
          ) : (
            items.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                compact
                onOpen={(x) => {
                  if (x.read_at === null) markRead.mutate(x.id)
                  setOpen(false)
                }}
              />
            ))
          )}
        </div>
        <div className="border-t p-1">
          <Button asChild variant="ghost" size="sm" className="w-full" onClick={() => setOpen(false)}>
            <Link to="/notifications">View all</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
